// A4 Component summary — every issue in one component.
//   * the two card rows are the filters (code list #4): same row switches, the two rows combine,
//     the same card again removes its filter
//   * more filters: Target date (Overdue / Select dates), Action by, search (× clears it)
//   * "Clear filters" removes every filter at once (shown only while a filter is on)
//   * tick rows → bulk bar: Status · Discipline · Target date · Void · Migrate → "Apply to N"
//   * click a row → A4.1 side panel; double-click → straight to the issue (A5)
//   * Issue ↑ / ↓ flips the order (round 13); ↑ / ↓ keys move a highlight through the rows — the side panel
//     follows it when open; Enter opens the issue; Space ticks the row (code list #8)
//   * "+" → New issue drawer with this component pre-filled (code list #7)
//   * Esc / Backspace: close panel / drawer / menu → else back to the previous page (code list #3)
//   * v0.6.0 Part 6: List / Plan — Plan shows the component's current plan with a numbered pin per issue (the filters
//     apply; voided hidden). Click a pin → side panel; double-click → the issue. Remembered on this PC.
// Data: issues_component({code, prefix}); plan view: issues_pins.
import { useEffect, useMemo, useState } from "react";
import { useListKeys } from "../../../core/useListKeys";
import { rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { href, navigate } from "../../../core/router";
import { DATE_HINT_ROW, formatDate } from "../../../core/format";
import { canEdit } from "../../../core/access";
import { useEsc } from "../../../core/esc";
import { CodeChip, DateInput, ErrorBox, Fab, Field, FilterDropdown, InfoIcon, Loading, Modal, PageHeader, Popover, SearchBox, Seg, SortTh, StatusPill, useAskClose } from "../../../core/ui/kit";
import type { ComponentSummary, IssueRow, IssueStatus } from "../../../core/api/types";
import { DisciplineCards, StatusCards } from "../components/Cards";
import { AddIssueDrawer } from "../components/AddIssueDrawer";
import { IssueSidePanel } from "../components/IssueSidePanel";
import { PlanCanvas, PlanToolbar } from "../components/Plan";
import { EDITABLE_STATUSES, EMPTY_FILTER, filterIssues, isFiltered, migrationPlan, overdueText, sortRows, toggleCard, type IssueFilter } from "../logic";
import { Icon } from "../../../core/ui/icons";

export const A4_TIP = ["Click a status or discipline card to filter the list.", "Click the same card again to remove its filter.",
  "Cards in the same row switch; the two rows combine.", "Clear filters removes all filters at once.",
  "Tick a checkbox to select rows for bulk edit, void or migrate.", "Click a row to open its side panel; double-click to open the issue.",
  "Click Issue ↑ / ↓ to flip the order (oldest or newest first).", "↑ / ↓ keys move between rows (the side panel follows); Enter opens the issue; Space ticks the row.",
  "Esc clears the ticked rows (after closing any pop-up and the side panel)."];

const VIEW_KEY = "buildnexx-a4-view";

export function ComponentPage({ code, prefix }: { code: string; prefix: string }) {
  const { data, error, loading, reload } = useRpc("issues_component", { code, prefix });
  const [f, setF0] = useState<IssueFilter>(EMPTY_FILTER);
  const [sel, setSel] = useState<string[]>([]);
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  // v0.8.1 (round 29 #1): the side panel / highlight follows the row's id, never its code — one click, one row
  const [panel, setPanel] = useState<string | null>(null);   // issue id
  const [adding, setAdding] = useState(false);
  const [view, setView0] = useState<"list" | "plan">(() => { try { return localStorage.getItem(VIEW_KEY) === "plan" ? "plan" : "list"; } catch { return "list"; } });
  const setView = (v: "list" | "plan") => { setView0(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window */ } };
  const rows = useMemo(() => (data ? sortRows(filterIssues(data.issues, f), dir) : []), [data, f, dir]);
  const [hi, setHi0] = useState<number | null>(null);
  const setHi = (i: number | null) => { setHi0(i); if (i !== null && panel && rows[i]) setPanel(rows[i].id); };
  useEffect(() => setHi0(null), [f, dir]);
  useListKeys({ count: rows.length, hi, setHi, active: !adding,
    onEnter: (i) => navigate({ name: "issue", code, prefix, issue: rows[i].code }),
    onSpace: (i) => { if (data && canEdit(data.access)) setSel((s) => (s.includes(rows[i].id) ? s.filter((x) => x !== rows[i].id) : [...s, rows[i].id])); } });
  // Esc / Backspace (round 14 #3): pop-ups close themselves first → side panel → ticked rows → previous page
  useEsc("popup", () => {
    if (panel) { setPanel(null); return true; }
    if (sel.length) { setSel([]); return true; }
    return false;
  }, !!panel || sel.length > 0);
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorBox message={error ?? "Not found"} />;
  const editor = canEdit(data.access);
  const setF = (patch: Partial<IssueFilter>) => setF0({ ...f, ...patch });
  const tick = (id: string) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const allTicked = rows.length > 0 && rows.every((r) => sel.includes(r.id));
  const panelRow = data.issues.find((r) => r.id === panel);
  const targetLabel = f.target === "range" && (f.from || f.to) ? `${formatDate(f.from)} – ${formatDate(f.to)}` : "";
  const open = (r: IssueRow) => navigate({ name: "issue", code, prefix, issue: r.code });

  return (
    <div className={panelRow ? "with-panel" : ""}>
      <div className="page-col">
        <PageHeader
          crumb={<>{data.project.name} <CodeChip code={code} /> › <a href={href({ name: "issues", code })}>Issues Tracker</a> › {data.component.name} ({prefix})</>}
          title={data.component.name}
          sub={editor ? undefined : "View only — your role cannot edit issues on this project"} />
        <StatusCards stats={data.stats} scope="component" selected={f.status} onSelect={(k) => setF({ status: toggleCard(f.status, k) })} />
        <DisciplineCards stats={data.stats} disciplines={data.disciplines} selected={f.discipline} onSelect={(c) => setF({ discipline: toggleCard(f.discipline, c) })} />
        <section className="card table-card">
          {editor && sel.length > 0 && <BulkBar data={data} ids={sel} onClear={() => setSel([])} onDone={() => { setSel([]); reload(); }} />}
          <div className="filters">
            <Seg label="View" value={view} onChange={setView} options={[{ value: "list", label: "List" }, { value: "plan", label: "Plan" }]} />
            <FilterDropdown allLabel="Target date" label="Target date filter" value={f.target}
              onChange={(x) => setF({ target: x as IssueFilter["target"], ...(x !== "range" ? { from: null, to: null } : {}) })}
              options={[{ value: "overdue", label: "Overdue" }, { value: "range", label: targetLabel || "Select dates…", stay: true }]}
              extra={() => f.target === "range" ? (
                <div className="range" onMouseDown={(e) => e.stopPropagation()}>
                  <DateInput ariaLabel="Target from" value={f.from} onChange={(d) => setF({ from: d })} /><span>–</span>
                  <DateInput ariaLabel="Target to" value={f.to} onChange={(d) => setF({ to: d })} />
                </div>) : null} />
            <FilterDropdown allLabel="Action by" label="Action by filter" value={f.actionBy} onChange={(x) => setF({ actionBy: x })}
              options={[...data.action_by.map((a) => ({ value: a, label: a })), { value: "–", label: "– (no log entry yet)" }]} />
            <SearchBox placeholder="Search issues" value={f.q} onChange={(q) => setF({ q })} width={220} />
            <InfoIcon tip={A4_TIP} />
            {isFiltered(f) && <button className="btn link accent" onClick={() => setF0(EMPTY_FILTER)}>Clear filters</button>}
            <span className="grow" /><span className="muted small" data-testid="row-count">{rows.length} of {data.issues.length}</span>
          </div>
          {view === "plan" ? <PlanPane code={code} prefix={prefix} codes={rows.map((r) => r.code)} active={panelRow?.code ?? null}
            onPin={(c) => { const k = rows.findIndex((r) => r.code === c); setPanel(rows[k]?.id ?? null); setHi0(k); }} onOpen={(c) => navigate({ name: "issue", code, prefix, issue: c })} /> : (
          <div className="scroll">
            <table data-testid="issues-table" className="issue-table">
              <thead><tr>
                <th className="cb">{editor && <input type="checkbox" aria-label="Tick all" checked={allTicked} onChange={() => setSel(allTicked ? [] : rows.map((r) => r.id))} />}</th>
                <SortTh label="Issue" arrow={dir === "asc" ? "↑" : "↓"} onClick={() => setDir(dir === "asc" ? "desc" : "asc")} />
                <th>Discipline</th><th>Date created</th><th>Target date</th><th>Overdue</th><th>Status</th><th>Latest action by</th>
              </tr></thead>
              <tbody>{rows.map((r, k) => (
                <tr key={r.id} data-issue={r.code} data-row={k} className={`clickable${r.status === "Voided" ? " voided" : ""}${sel.includes(r.id) ? " ticked" : ""}${panel === r.id ? " active" : ""}${k === hi ? " kbd" : ""}`}
                  onClick={() => { setPanel(r.id); setHi0(k); }} onDoubleClick={() => open(r)}>
                  <td className="cb" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>{editor && <input type="checkbox" aria-label={`Tick ${r.code}`} checked={sel.includes(r.id)} onChange={() => tick(r.id)} />}</td>
                  <td className="code nowrap">{r.code}</td>
                  <td>{r.discipline}</td>
                  <td className="nowrap">{formatDate(r.created_on)}</td>
                  <td className="nowrap">{formatDate(r.target_date)}</td>
                  <td className={`nowrap${r.overdue_days ? " overdue" : " faint"}`}>{overdueText(r.overdue_days)}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td>{r.latest_action_by || "–"}</td>
                </tr>))}
                {rows.length === 0 && <tr><td colSpan={8} className="muted pad">No issues match these filters.</td></tr>}
              </tbody>
            </table>
          </div>)}
        </section>
        {editor && <Fab label="Add issue" onClick={() => setAdding(true)} />}
      </div>
      {panelRow && <IssueSidePanel row={panelRow} code={code} prefix={prefix} disciplines={data.disciplines} onClose={() => setPanel(null)} />}
      {adding && <AddIssueDrawer code={code} components={data.components} disciplines={data.disciplines} prefix={prefix} today={data.today}
        onClose={() => setAdding(false)} onCreated={(pf, issue) => navigate({ name: "issue", code, prefix: pf, issue })} />}
    </div>
  );
}

/** Bulk bar (A4): pick values → "Apply to N"; Void and Migrate open their own pop-ups. Voided issues are skipped by the database. */
/** A4 Plan view: the current plan with the pins of the listed issues (filters apply). */
function PlanPane({ code, prefix, codes, active, onPin, onOpen }: { code: string; prefix: string; codes: string[]; active: string | null; onPin: (c: string) => void; onOpen: (c: string) => void }) {
  const { data, error } = useRpc("issues_pins", { code, prefix });
  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading />;
  if (!data.plan) return <p className="muted pad" data-testid="no-plan">No plan for this component yet — upload one in Project setup › Components › Plan.</p>;
  const shown = data.pins.filter((p) => codes.includes(p.code));
  const unpinned = shown.filter((p) => p.x === null).length;
  return (
    <div className="plan-pane">
      <PlanToolbar name={data.plan.name} version={data.plan.version} extra={unpinned > 0 ? <span className="muted tiny" data-testid="unpinned">{unpinned} without a pin</span> : undefined} />
      <div className="plan-scroll">
        <PlanCanvas path={data.plan.path} page={data.plan.page} rotation={data.plan.rotation} pins={shown} active={active} onPin={(p) => onPin(p.code)} onPinOpen={(p) => onOpen(p.code)} />
      </div>
    </div>
  );
}

function BulkBar({ data, ids, onClear, onDone }: { data: ComponentSummary; ids: string[]; onClear: () => void; onDone: () => void }) {
  const [p, setP] = useState<{ status?: IssueStatus; discipline?: string; target_date?: string | null }>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"void" | "migrate" | null>(null);
  const [badDate, setBadDate] = useState(false);
  const picked = data.issues.filter((r) => ids.includes(r.id));
  const apply = async () => {
    if (badDate) return setMsg(`Target date: ${DATE_HINT_ROW}`);
    if (Object.keys(p).length === 0) return setMsg("Pick a value to change first");
    try { await rpc("issues_bulk_update", { code: data.project.code, ids, ...p }); setP({}); setMsg(null); onDone(); }
    catch (e) { setMsg((e as Error).message); }
  };
  const pick = (label: string, value: string | undefined, opts: string[], key: "status" | "discipline") => (
    <Popover label={label} className={`filter${value ? " on" : ""}`} button={() => <span className="filter-btn">{value ? `${label}: ${value}` : label} <Icon name="arrow_drop_down" /></span>}>
      {(close) => opts.map((o) => <button key={o} className={`menu-item${o === value ? " current" : ""}`} onClick={() => { setP({ ...p, [key]: o }); close(); }}>{o}</button>)}
    </Popover>
  );
  return (
    <div className="bulk-bar" data-testid="bulk-bar">
      <b>{ids.length} selected</b>
      {pick("Status", p.status, EDITABLE_STATUSES, "status")}
      {pick("Discipline", p.discipline, data.disciplines.map((d) => d.code), "discipline")}
      <Popover label="Target date" className={`filter${p.target_date !== undefined ? " on" : ""}`}
        button={() => <span className="filter-btn">{p.target_date !== undefined ? `Target: ${formatDate(p.target_date)}` : "Target date"} <Icon name="arrow_drop_down" /></span>}>
        {() => <div className="range"><DateInput ariaLabel="New target date" value={p.target_date ?? null} onChange={(d) => setP({ ...p, target_date: d })} onValidity={(ok) => setBadDate(!ok)} /></div>}
      </Popover>
      <button className="btn danger" onClick={() => setDialog("void")}>Void</button>
      <button className="btn" onClick={() => setDialog("migrate")}>Migrate</button>
      {msg && <span className="error-inline">{msg}</span>}
      <span className="grow" />
      <button className="btn link" onClick={onClear}>Clear</button>
      <button className="btn primary" onClick={apply}>Apply to {ids.length}</button>
      {dialog === "void" && <BulkVoidModal code={data.project.code} picked={picked} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onDone(); }} />}
      {dialog === "migrate" && <MigrateModal data={data} picked={picked} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onDone(); }} />}
    </div>
  );
}

/** Bulk void (comment #24): same pop-up as on A5 — a reason is required and applies to every ticked issue. */
function BulkVoidModal({ code, picked, onClose, onDone }: { code: string; picked: IssueRow[]; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { close, confirm } = useAskClose(!!reason.trim(), onClose);      // no "Save" here: voiding is never a default (v0.4.3)
  const live = picked.filter((r) => r.status !== "Voided");
  const go = async () => {
    try { await rpc("issues_bulk_void", { code, ids: picked.map((r) => r.id), reason }); onDone(); } catch (e) { setError((e as Error).message); }
  };
  return (
    <Modal title={`Void ${live.length} ${live.length === 1 ? "issue" : "issues"}?`} onClose={close}>
      <p className="muted">Voiding retires an issue. It stays in the list (greyed out), is excluded from totals, and cannot be edited or re-opened. Its code is never reused.</p>
      {picked.length > live.length && <p className="muted small">{picked.length - live.length} ticked issue already voided — skipped.</p>}
      {error && <ErrorBox message={error} />}
      <Field label="Reason (applies to all) *"><textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Void reason" /></Field>
      <div className="modal-foot"><button className="btn" onClick={close}>Cancel</button><button className="btn danger" disabled={live.length === 0} onClick={go}>Void {live.length} {live.length === 1 ? "issue" : "issues"}</button></div>
      {confirm}
    </Modal>
  );
}

/** Migrate (comment #26, board A4.3): ticked OPEN issues move to another component with their next numbers. */
function MigrateModal({ data, picked, onClose, onDone }: { data: ComponentSummary; picked: IssueRow[]; onClose: () => void; onDone: () => void }) {
  const others = data.components.filter((c) => c.prefix !== data.component.prefix);
  const [to, setTo] = useState(others[0]?.prefix ?? "");
  const [copyLog, setCopyLog] = useState(true);
  const [keepDate, setKeepDate] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const target = data.components.find((c) => c.prefix === to);
  const plan = migrationPlan(picked, target?.next_code ?? null);
  const go = async () => {
    try { await rpc("issues_migrate", { code: data.project.code, ids: picked.map((r) => r.id), to, copy_log: copyLog, keep_date: keepDate }); onDone(); }
    catch (e) { setError((e as Error).message); }
  };
  return (
    <Modal title={`Migrate ${plan.pairs.length} ${plan.pairs.length === 1 ? "issue" : "issues"}`} onClose={onClose} className="natural">
      <div className="row gap6 small muted"><InfoIcon tip={["Only Open and Critical issues move; closed and voided ones are skipped.", "Each issue is voided here and created in the new component with its next number.",
        "Status, discipline, dates, gridline, location and references are copied.", "Nothing changes until you press Migrate."]} /> What moves where</div>
      {error && <ErrorBox message={error} />}
      <Field label="Move to component">
        <select aria-label="Move to component" value={to} onChange={(e) => setTo(e.target.value)}>
          {others.map((c) => <option key={c.prefix} value={c.prefix}>{c.name} ({c.prefix})</option>)}
        </select>
      </Field>
      <div className="migrate-list" data-testid="migrate-list">
        {plan.pairs.map(([a, b]) => <div key={a} className="row between"><span><span className="code">{a}</span> <span className="muted">voided here</span></span><span>→ <span className="code">{b}</span> <span className="muted">new</span></span></div>)}
        {plan.skipped > 0 && <p className="muted small">{plan.skipped} ticked {plan.skipped === 1 ? "issue" : "issues"} skipped (closed or voided)</p>}
      </div>
      <label className="check"><input type="checkbox" checked={copyLog} onChange={(e) => setCopyLog(e.target.checked)} aria-label="Copy resolution log" /> Copy resolution log</label>
      <p className="small muted">Date created of the new issues</p>
      <div className="row gap6 radios">
        <label className="check"><input type="radio" name="mdate" checked={keepDate} onChange={() => setKeepDate(true)} aria-label="Keep original date" /> Keep original date</label>
        <label className="check"><input type="radio" name="mdate" checked={!keepDate} onChange={() => setKeepDate(false)} aria-label="Use today" /> Use today ({formatDate(data.today)})</label>
      </div>
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={plan.pairs.length === 0 || !to} onClick={go}>Migrate {plan.pairs.length} {plan.pairs.length === 1 ? "issue" : "issues"}</button></div>
    </Modal>
  );
}

